import { format, subDays, startOfDay, endOfDay } from 'date-fns'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Button } from '@/common/ui/button'
import { Label } from '@/common/ui/label'
import { parseAsString, useQueryStates } from 'nuqs'

export interface MessagingAnalyticsFiltersState {
	startDate: string
	endDate: string
	messageType: string
	period: string
}

interface MessagingAnalyticsFiltersProps {
	onChange?: (filters: MessagingAnalyticsFiltersState) => void
}

export default function MessagingAnalyticsFilters({
	onChange,
}: MessagingAnalyticsFiltersProps) {
	const today = new Date()
	const ninetyDaysAgo = subDays(today, 90)

	const [queryState, setQueryState] = useQueryStates(
		{
			startDate: parseAsString.withDefault(format(ninetyDaysAgo, 'yyyy-MM-dd')),
			endDate: parseAsString.withDefault(format(today, 'yyyy-MM-dd')),
			messageType: parseAsString.withDefault(''),
			period: parseAsString.withDefault('90'),
		},
		{ shallow: false },
	)

	// Handle period change
	const handlePeriodChange = (period: string) => {
		let startDate: string
		const endDate = format(endOfDay(new Date()), 'yyyy-MM-dd')

		if (period === 'all') {
			startDate = '' // Empty means all time
		} else {
			const days = parseInt(period, 10)
			startDate = format(startOfDay(subDays(new Date(), days)), 'yyyy-MM-dd')
		}

		const newState = {
			...queryState,
			startDate,
			endDate,
			period,
		}

		setQueryState(newState)
		onChange?.(newState)
	}

	const handleMessageTypeChange = (value: string) => {
		const newState = { ...queryState, messageType: value }
		setQueryState(newState)
		onChange?.(newState)
	}

	return (
		<div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-6">
			{/* Period Buttons */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">ช่วงเวลา</Label>
				<div className="flex flex-wrap gap-2">
					<Button
						variant={queryState.period === '30' ? 'default' : 'outline'}
						size="sm"
						onClick={() => handlePeriodChange('30')}
					>
						30 วัน
					</Button>
					<Button
						variant={queryState.period === '90' ? 'default' : 'outline'}
						size="sm"
						onClick={() => handlePeriodChange('90')}
					>
						90 วัน
					</Button>
					<Button
						variant={queryState.period === 'all' ? 'default' : 'outline'}
						size="sm"
						onClick={() => handlePeriodChange('all')}
					>
						ทั้งหมด
					</Button>
				</div>
			</div>

			{/* Message Type Dropdown */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">ประเภทข้อความ</Label>
				<Select
					value={queryState.messageType}
					onValueChange={handleMessageTypeChange}
				>
					<SelectTrigger className="w-full lg:w-[180px]">
						<SelectValue placeholder="ทั้งหมด" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="">ทั้งหมด</SelectItem>
						<SelectItem value="IMMEDIATE">ทันที</SelectItem>
						<SelectItem value="SCHEDULED">ตามกำหนด</SelectItem>
						<SelectItem value="AUTO">อัตโนมัติ</SelectItem>
						<SelectItem value="INTERVAL">ตามช่วงเวลา</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</div>
	)
}
